










































































































































import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import { ActionAPI } from '@/api/backend-client'
import { Action } from '@/api/model/action'
import { ActionType } from '@/api/types'
import { User } from '@/api/model/user'
import { isAllowedUserForReferral } from '@/helpers/security'
import { apiDate, groupByDay, isOverdueTask, overdueWindow } from '@/helpers/overdue-actions'
import { fetchPagesInOrder, PagedResult } from '@/helpers/paging'
import moment from 'moment'

// The API allows at most 100 per page; newest first, so a cut-off drops the oldest overdue tasks.
// The cap counts API rows: appointments and tasks on won or lost leads are only dropped here, after loading.
const OVERDUE_PAGE_SIZE = 100
const OVERDUE_MAX_PAGES = 3


@Component
export default class ActionsBlock extends Vue {
    @Prop({ default: false }) meetings!: boolean
    @Prop({ required: true }) startDate!: Date
    @Prop({ required: true }) endDate!: Date
    @Prop({ default: false }) loading!: boolean
    @Prop({ default: () => [] }) actions!: Action[]
    view: string = 'user'
    overdueActions: Action[] = []
    overdueLoaded: boolean = false
    overdueTruncated: boolean = false
    olderOverdueLoading: boolean = false
    olderOverdueLoaded: boolean = false
    olderOverdueFailed: boolean = false
    overdueFailed: boolean = false
    overdueIncomplete: boolean = false
    overdueRequest: number = 0

    mounted() {
        // Through changeView: a stored default the user may no longer use (a former team lead's "Voor mijn teams")
        // must not load the team's tasks
        this.changeView(this.$currentUser.defaultDashboardView)
    }

    /**
     * What the overdue list depends on. The parent sets startDate in its own mounted(), which runs after this one,
     * and it can arrive in the same tick as a view change: a watcher on this key loads once per change, and not
     * before there is a "today" to count from.
     */
    get overdueQueryKey(): string|null {
        if (this.meetings || !this.startDate) {
            return null
        }

        return `${this.view}|${apiDate(this.startDate)}`
    }

    @Watch('overdueQueryKey', { immediate: true })
    onOverdueQueryChanged(key: string|null): void {
        if (key) {
            this.loadOverdueActions()
        }
    }

    get openActionParams(): object {
        return {
            // keyvue-api's `completed` filter is an exact SearchFilter: send 0/1 ("true" would match open actions)
            'completed': 0,
            'view': this.view,
            'order[actionDate]': 'desc',
        }
    }

    /** Newest first, so whatever a cut-off or failed page leaves out is always the oldest (see fetchPagesInOrder) */
    fetchOpenActions(params: object): Promise<PagedResult<Action>> {
        return fetchPagesInOrder(page => ActionAPI.getPaged({
            ...this.openActionParams,
            ...params,
            pagination: true,
            itemsPerPage: OVERDUE_PAGE_SIZE,
            page,
        }), OVERDUE_MAX_PAGES)
    }

    async loadOverdueActions(): Promise<void> {
        // Same rule as the watcher: not for the meetings block, and not before there is a "today" to count from
        if (!this.overdueQueryKey) {
            return
        }

        const request = ++this.overdueRequest

        // Nothing from the previous load (another view) may be shown, offered or added while this one runs
        this.overdueActions = []
        this.overdueLoaded = false
        this.overdueFailed = false
        this.overdueIncomplete = false
        this.overdueTruncated = false
        this.olderOverdueLoading = false
        this.olderOverdueLoaded = false
        this.olderOverdueFailed = false

        // The upcoming list's first day, so both lists agree on "today" even after midnight
        const { after, strictlyBefore } = overdueWindow(this.startDate)

        const recent = await this.fetchOpenActions({
            'actionDate[after]': apiDate(after),
            'actionDate[strictly_before]': apiDate(strictlyBefore),
        })

        // A newer request (the view changed meanwhile) wins.
        if (request !== this.overdueRequest) {
            return
        }

        this.overdueActions = recent.items
        this.overdueFailed = recent.failed && recent.items.length === 0
        // Pages are newest first, so a missing page 2 or 3 means the oldest are missing; a retry can fill them in
        this.overdueIncomplete = recent.failed && recent.items.length > 0
        this.overdueTruncated = recent.truncated || this.overdueIncomplete
        this.overdueLoaded = !this.overdueFailed
    }

    async loadOlderOverdueActions(): Promise<void> {
        if (!this.olderOverdueOfferable) {
            return
        }

        const request = this.overdueRequest
        this.olderOverdueLoading = true
        this.olderOverdueFailed = false

        const older = await this.fetchOpenActions({
            'actionDate[strictly_before]': apiDate(overdueWindow(this.startDate).after),
        })

        // loadOverdueActions resets these flags for the newer request
        if (request !== this.overdueRequest) {
            return
        }

        this.olderOverdueLoading = false

        if (older.failed && older.items.length === 0) {
            // Nothing came back: offer the link again
            this.olderOverdueFailed = true
            return
        }

        // A failed later page only costs the oldest ones (fetchOpenActions keeps the unbroken run from page 1), which
        // the cut-off line says. There's no retry for that: it would have to throw away what did load.
        this.overdueActions = [...older.items, ...this.overdueActions]
        this.overdueTruncated = this.overdueTruncated || older.truncated || older.failed
        this.olderOverdueLoaded = true
    }

    /** Whether the overdue list holds a task from before the 30-day window, i.e. one that "Toon oudere" added */
    get hasOlderOverdueTasks(): boolean {
        const windowStart = overdueWindow(this.startDate).after

        return this.overdueTasks.some(action => moment(action.actionDate).isBefore(windowStart))
    }

    /** The state of "Toon oudere open taken", shown next to the link in the red bar or the footer */
    get olderOverdueNote(): string|null {
        if (this.olderOverdueLoading) {
            return String(this.$t('dashboard.actions.loadingOlder'))
        }

        if (this.olderOverdueFailed) {
            return String(this.$t('dashboard.actions.loadFailed'))
        }

        // Not after a cut-off: then the line below already says the oldest are missing
        if (this.olderOverdueLoaded && !this.hasOlderOverdueTasks && !this.overdueTruncated) {
            return String(this.$t('dashboard.actions.noOlder'))
        }

        return null
    }

    /**
     * Older tasks are only offered once the last 30 days loaded completely: after a cut-off they would leave a gap
     * between the older tasks and the newest ones.
     */
    get olderOverdueOfferable(): boolean {
        return !this.meetings
            && this.overdueLoaded
            && !this.overdueTruncated
            && !this.olderOverdueLoading
            && !this.olderOverdueLoaded
    }

    /** In "Voor mij", only actions assigned to the current user; the upcoming and overdue lists share this rule */
    isVisibleInView(action: Action): boolean {
        if (this.view === 'user') {
            return action.assignedTo.some((user: User) => {
                return user.id === this.$currentUser.id
            })
        }

        return true
    }

    // The API already applies the view; isVisibleInView also hides colleagues' tasks right away while a switch
    // from "Voor mijn teams" to "Voor mij" reloads.
    isOverdueForView(action: Action): boolean {
        return isOverdueTask(action, this.startDate) && this.isVisibleInView(action)
    }

    get overdueTasks(): Action[] {
        if (this.meetings) {
            return []
        }

        return this.overdueActions.filter(action => this.isOverdueForView(action))
    }

    get overdueTitle(): string {
        const count = `${this.overdueTasks.length}${this.overdueTruncated ? '+' : ''}`

        return String(this.$t('dashboard.actions.overdue', { count }))
    }

    get dayGroups(): Array<{ date: string, actions: Action[], overdue: boolean }> {
        const overdueByDay = groupByDay(this.overdueTasks)
        const upcomingByDay = this.filteredActionsByDate

        return [
            ...Object.keys(overdueByDay).map(date => ({ date, actions: overdueByDay[date], overdue: true })),
            ...Object.keys(upcomingByDay).map(date => ({ date, actions: upcomingByDay[date], overdue: false })),
        ]
    }

    get blockKey(): string {
        if (this.meetings) {
            return 'meetings'
        } else {
            return 'actions'
        }
    }

    get availableViews(): string[] {
        return this.$currentUser.isTeamLead ? ['user', 'branches'] : ['user']
    }

    get availableViewsOptions() {
        return this.availableViews
            .map(availableView => ({
                label: this.$t(`dashboard.actions.view.${availableView}`),
                value: availableView,
            }))
    }

    changeView(view: string): void {
        if (!this.availableViews.includes(view)) {
            return
        }

        this.view = view
    }

    get filteredActions(): Action[] {
        return this.actions
            .filter((action: Action) => {
                if (!this.meetings && (action.type === ActionType.MEETING || action.completed)) {
                    return false
                } else if (this.meetings && (action.type !== ActionType.MEETING)) {
                    return false
                }

                if (!moment(action.actionDate).isSameOrAfter(this.startDate, 'day')) {
                    return false
                }

                if (!moment(action.actionDate).isSameOrBefore(this.endDate, 'day')) {
                    return false
                }

                return this.isVisibleInView(action)
            })
            .sort((a: Action, b: Action) => {
                return moment(a.actionDate).unix() - moment(b.actionDate).unix()
            })
    }

    get filteredActionsByDate() {
        const actionsByDate = {}

        let date = moment(this.startDate)
        while (date.isSameOrBefore(this.endDate, 'day')) {
            actionsByDate[date.format('YYYY-MM-DD')] = []
            date = moment(date).add('1', 'day')
        }

        this.filteredActions
            .forEach((action: Action) => {
                const actionDate = moment(action.actionDate).format('YYYY-MM-DD')
                if (actionDate in actionsByDate) {
                    actionsByDate[actionDate].push(action)
                }
            })

        return actionsByDate
    }

    get maxVisibleActions(): false|number {
        if (this.view === 'user') {
            return false
        }

        return 1
    }

    visibleActions(actions: Action[]): Action[] {
        if (this.maxVisibleActions === false) {
            return actions
        }

        return actions.slice(0, this.maxVisibleActions)
    }

    isMonthHeaderVisible(date: string): boolean {
        if (moment(date).isSame(this.startDate, 'day')) {
            return true
        }

        if (moment(date).format('D') === '1') {
            return true
        }

        return false
    }

    goToDetail(action: Action) {
        if (!isAllowedUserForReferral(this.$currentUser, action.referral) && this.$currentUser.betaOptInEnabled) {
            return this.$router.push({
                name: 'one_contact_detail',
                params: {
                    contactId: action.referral.contact.id,
                    toOne: this.$currentUser.betaOptInEnabled as unknown as string,
                },
            })
        }

        return this.$router.push({
            name: 'tasks_detail',
            params: {
                referralId: action.referral.id,
                contactId: action.referral.contact.id,
                toOne: this.$currentUser.betaOptInEnabled as unknown as string,
            },
        })
    }

    colorForAction(action: Action): string {
        return 'gray'
    }

    classesForAction(action: Action): string[] {
        return [
            `bg-${this.colorForAction(action)}-100`,
        ]
    }

    iconForAction(action: Action): string {
        if (this.meetings) {
            // Determine icon based on appointment type
            switch (action.appointmentType) {
                case 'video': return 'icon-meeting-action-video'
                case 'phone': return 'icon-phone-retro'
                case 'office': return 'icon-meeting-action-office'
                case 'customer': return 'icon-meeting-action-customer'
                default: return 'icon-meeting-action'
            }
        } else {
            // Determine icon based on type
            switch (action.type) {
                case 'meeting': return 'icon-meeting-action'
                case 'email': return 'icon-email-action'
                case 'call': return 'icon-call-action'
                case 'reminder': return 'icon-reminder-action'
                case 'letter': return 'icon-letter-action'
                case 'billing': return 'icon-billing'
                case 'update': return 'icon-update-action'
                case 'notification': return 'icon-reminder-action'
                default: return 'icon-task-action'
            }
        }
    }

    iconClassesForAction(action: Action): string[] {
        return [
            `bg-${this.colorForAction(action)}-100`,
            `text-${this.colorForAction(action)}-600`,
        ]
    }
}
